// src/components/common/LoadingScreen.js
import React, { useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, useAnimation } from 'framer-motion';
import { ThemeContext } from '../../context/ThemeContext';

// Container principal avec un fond légèrement translucide
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  backdrop-filter: blur(5px);
  background: ${props => props.isDark 
    ? 'rgba(10, 20, 40, 0.8)' 
    : 'rgba(240, 245, 255, 0.8)'
  };
`;

// Card container pour le contenu
const LoadingCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => props.isDark 
    ? 'rgba(20, 30, 50, 0.7)' 
    : 'rgba(255, 255, 255, 0.7)'
  };
  border-radius: 16px;
  padding: 3rem 4rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.isDark 
    ? 'rgba(60, 100, 150, 0.2)' 
    : 'rgba(200, 220, 255, 0.5)'
  };
  backdrop-filter: blur(10px);
  min-width: 300px;
`;

// Logo et titre
const LogoContainer = styled(motion.div)`
  margin-bottom: 2.5rem;
  font-size: 2.2rem;
  font-weight: 700;
  letter-spacing: -0.5px;
  color: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg, 
      transparent, 
      ${props => props.theme.primary}80, 
      ${props => props.theme.primary}, 
      ${props => props.theme.primary}80, 
      transparent
    );
  }
`;

// Animation des points de neurones
const NeuronDotsContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
  height: 50px;
`;

const NeuronDot = styled(motion.div)`
  width: ${props => props.size || 12}px;
  height: ${props => props.size || 12}px;
  border-radius: 50%;
  background: ${props => props.color || props.theme.primary};
  margin: 0 6px;
  box-shadow: 0 0 10px ${props => props.color || props.theme.primary}80;
`;

// Ligne de connexion entre les points
const NeuronConnection = styled(motion.div)`
  height: 2px;
  background: ${props => props.theme.primary}50;
  position: absolute;
  top: 50%;
  transform-origin: left center;
`;

// Message de chargement
const LoadingMessage = styled(motion.p)`
  margin-top: 1.5rem;
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

// Barre de progression
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: ${props => props.theme.primary}20;
  border-radius: 2px;
  margin-top: 1.5rem;
  overflow: hidden;
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  background: linear-gradient(
    90deg,
    ${props => props.theme.primary}80,
    ${props => props.theme.primary},
    ${props => props.theme.accent || props.theme.primary}
  );
  border-radius: 2px;
`;

const LoadingScreen = () => {
  const { theme, isDarkMode } = useContext(ThemeContext);
  const controls = useAnimation();
  
  // Animation de progression
  useEffect(() => {
    const animateProgress = async () => {
      await controls.start({
        width: '100%',
        transition: { duration: 3, ease: "easeInOut" }
      });
    };
    
    animateProgress();
  }, [controls]);
  
  // Configuration des points de neurones
  const neuronDots = [
    { size: 8, delay: 0.2, color: theme.primary },
    { size: 14, delay: 0, color: theme.primary },
    { size: 10, delay: 0.1, color: theme.accent || theme.primary },
    { size: 16, delay: 0.3, color: theme.primary },
    { size: 12, delay: 0.4, color: theme.accent || theme.primary }
  ];
  
  return (
    <LoadingContainer isDark={isDarkMode}>
      <LoadingCard 
        isDark={isDarkMode}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <LogoContainer
          theme={theme}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          PropentaTech
        </LogoContainer>
        
        <NeuronDotsContainer>
          {neuronDots.map((dot, index) => (
            <NeuronDot
              key={index}
              theme={theme}
              size={dot.size}
              color={dot.color}
              initial={{ y: 0, opacity: 0.5 }}
              animate={{ 
                y: [0, -15, 0], 
                opacity: [0.5, 1, 0.5],
                scale: [1, 1.2, 1]
              }}
              transition={{ 
                duration: 1.2, 
                delay: dot.delay, 
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </NeuronDotsContainer>
        
        <LoadingMessage
          theme={theme}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          Initialisation du réseau...
        </LoadingMessage>
        
        <ProgressBarContainer theme={theme}>
          <ProgressBar 
            theme={theme}
            initial={{ width: '5%' }}
            animate={controls}
          />
        </ProgressBarContainer>
      </LoadingCard>
    </LoadingContainer>
  );
};

export default LoadingScreen;